import { useAtomValue, useSetAtom } from "jotai";
import {
    AccountPanelViewAtom,
    newAccountsAtom,
    selectedAccountAtom,
} from "@/features/Labeling/Labeling.state";
import {
    AccountPanelView,
    LabelListStatementType,
} from "@/features/Labeling/Labeling.types";
import { RawInput } from "@/ui";
import { useCallback, useState } from "react";
import {
    AccountCategory,
    AccountLabel,
} from "@/entities/DataAssociation/Account.model";
import { StatementTypeMenu } from "../StatementTypeMenu";
import { labelsByStatementType } from "../../constants/labelsByStatementType";
import { LabelCategoryMenu } from "../LabelCategoryMenu";
import { ACCOUNT_LIST } from "../../constants/labeling.constants";
import { LabelList } from "../LabelList";
import clsx from "clsx";
import { useRowSelection } from "../../hooks/useRowSelection";
import {
    AccountHintType,
    DraftAccount,
} from "@/entities/DataAssociation/Draft.model";

export function NewAccount({
    statementType,
    setStatementType,
    hint,
}: {
    statementType: LabelListStatementType;
    setStatementType: (statementType: LabelListStatementType) => void;
    hint?: DraftAccount;
}) {
    const setView = useSetAtom(AccountPanelViewAtom);
    const setNewAccounts = useSetAtom(newAccountsAtom);
    const { onAccountSelection } = useRowSelection();
    const selectedRow = useAtomValue(selectedAccountAtom);
    const [accountName, setAccountName] = useState<string | null>(
        selectedRow?.rowHeader || null,
    );
    const [selectedLabel, setAccountLabel] = useState<AccountLabel | null>(
        null,
    );
    const categories = Object.keys(
        labelsByStatementType[statementType],
    ) as AccountCategory[];

    const onAccountCreation = useCallback(() => {
        if (!accountName || !selectedLabel) return;
        setNewAccounts((prev) => [
            ...prev,
            {
                id: "",
                accountName: accountName || "",
                label: selectedLabel || "",
                companyId: "",
                labelSource: "",
                ignore: false,
                isoCurrencyCode: "",
                createdBy: "",
                createdAt: "",
                identifiers: [],
            },
        ]);
        onAccountSelection({
            type: AccountHintType.NEW,
            accountName: accountName || "",
            label: selectedLabel,
        });
        setView(AccountPanelView.ACCOUNT_LIST);
    }, [accountName, selectedLabel, setNewAccounts, onAccountSelection]);

    return (
        <>
            <RawInput
                name="accountName"
                label="Account Name"
                showLabel={false}
                value={accountName || ""}
                onChange={(e) => setAccountName(e.target.value)}
                className="mb-6"
            />
            <>
                <StatementTypeMenu
                    statementType={statementType}
                    setStatementType={setStatementType}
                />
                <LabelCategoryMenu
                    categories={categories}
                    initalCategory={
                        hint?.label
                            ? ACCOUNT_LIST[hint.label].category
                            : undefined
                    }
                >
                    {(props) => (
                        <LabelList
                            labels={Object.values(ACCOUNT_LIST)
                                .filter(
                                    (label) =>
                                        label.category ===
                                        props.selectedCategory,
                                )
                                .sort((a, b) => {
                                    if (hint?.label) {
                                        if (a.code === hint.label) return -1;
                                        if (b.code === hint.label) return 1;
                                    }
                                    return a.name.localeCompare(b.name);
                                })}
                            onLabelSelection={setAccountLabel}
                            selectedLabel={selectedLabel}
                            recommendedLabels={hint?.label ? [hint.label] : []}
                        />
                    )}
                </LabelCategoryMenu>
            </>
            <div
                className={clsx(
                    "bg-default w-full p-6",
                    "absolute inset-x-0 bottom-0",
                )}
            >
                <button
                    className="primary xl w-full"
                    disabled={!selectedLabel || !accountName}
                    onClick={onAccountCreation}
                >
                    Create account
                </button>
            </div>
        </>
    );
}
